import React, { useState, useContext, useEffect } from 'react';
import api from '../api/axios';
import { AuthContext } from '../context/AuthContext';
import { 
    Shield, UserCheck, Stethoscope, Activity, Hexagon, FlaskConical, 
    ArrowLeft, Plus, User, Loader, UserPlus, Edit2, Trash2, Lock, Unlock, CheckCircle, Search
} from 'lucide-react';

const REGIONS_DATA = {
    "г. Душанбе": ["р. Исмоили Сомони", "р. Сино", "р. Фирдавси", "р. Шохмансур"],
    "Согдийская область": ["Худжанд", "Исфара", "Истаравшан", "Канибадам", "Пенджикент", "Гулистон", "Б. Гафуров"],
    "Хатлонская область": ["Бохтар (Курган-Тюбе)", "Куляб", "Нурек", "Дангара", "Яван", "Фархор", "Восе"],
    "Районы республиканского подчинения": ["Вахдат", "Гиссар", "Турсунзаде", "Рашт", "Рогон", "Рудаки"],
    "Горно-Бадахшанская автономная область": ["Хорог", "Дарваз", "Вандж", "Рушан", "Ишкашим", "Мургаб"]
};

const formatPhone = (value) => {
    if (!value) return '+992 ';
    const numbers = value.replace(/\D/g, '');
    const tjNum = numbers.startsWith('992') ? numbers.slice(3) : numbers;
    let res = '+992 ';
    if (tjNum.length > 0) res += tjNum.substring(0, 3);
    if (tjNum.length > 3) res += '-' + tjNum.substring(3, 5);
    if (tjNum.length > 5) res += '-' + tjNum.substring(5, 7);
    if (tjNum.length > 7) res += '-' + tjNum.substring(7, 9);
    return res;
};

const formatAddress = (addressStr) => {
    if (!addressStr) return "";
    if (addressStr.includes('|||')) return addressStr.split('|||').filter(Boolean).join(', ');
    return addressStr;
};

const Registration = () => {
    const { user } = useContext(AuthContext);

    const [view, setView] = useState('cards'); 
    const [selectedRole, setSelectedRole] = useState(null); 
    const [usersList, setUsersList] = useState([]); 
    const [hospitals, setHospitals] = useState([]);
    const [hospitalDepartments, setHospitalDepartments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editingUserId, setEditingUserId] = useState(null); 

    const initialFormState = {
        full_name: '', email: '', password: '', phone: '+992 ',
        position: '', hospital_id: '', department: '', specialization: '', cabinet: '', license_number: '',
        organization_name: '', region: '', city: '', street: '', lab_type: 'Клиническая'
    };
    const [formData, setFormData] = useState(initialFormState);

    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [visibleCount, setVisibleCount] = useState(10);

    useEffect(() => {
        setVisibleCount(10);
    }, [selectedRole, searchQuery, statusFilter]);

    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 200) {
                setVisibleCount(prev => prev + 10);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const roleConfig = {
        admin: { title: "Администратор", icon: <User size={20}/>, desc: "Полный доступ к системе" },
        moderator: { title: "Модератор", icon: <UserCheck size={20}/>, desc: "Регистрация аптек и лабораторий" },
        ministry: { title: "Представитель Минздрава", icon: <Shield size={20}/>, desc: "Регистрация главврачей" },
        chief_doctor: { title: "Главный врач больницы", icon: <Activity size={20}/>, desc: "Управление больницей" },
        doctor: { title: "Врач", icon: <Stethoscope size={20}/>, desc: "Лечение пациентов" },
        pharmacy: { title: "Аптека", icon: <Hexagon size={20}/>, desc: "Выдача лекарств" },
        lab: { title: "Лаборатория", icon: <FlaskConical size={20}/>, desc: "Проведение анализов" }
    };

    useEffect(() => {
        const loadHospitals = async () => {
            try {
                const res = await api.get('/hospitals');
                setHospitals(res.data);
            } catch (e) { console.error("Ошибка загрузки больниц"); }
        };
        loadHospitals();
    }, []);

    const canRegister = (targetRole) => {
        const myRole = user?.role;
        if (myRole === 'super_admin' || myRole === 'admin') return true; 
        if (myRole === 'ministry') return ['chief_doctor', 'doctor', 'pharmacy', 'lab'].includes(targetRole);
        if (myRole === 'chief_doctor') return targetRole === 'doctor';
        if (myRole === 'moderator') return ['pharmacy', 'lab'].includes(targetRole);
        return false;
    };

    const handleCardClick = (roleKey) => {
        if (!canRegister(roleKey)) return alert("Нет прав доступа");
        setSelectedRole(roleKey);
        fetchUsers(roleKey);
        setView('list');
    };

    const fetchUsers = async (role) => {
        setIsLoading(true);
        try {
            const res = await api.get(`/users?role=${role}`);
            setUsersList(res.data);
        } catch (error) { console.error(error); } finally { setIsLoading(false); }
    };

    const handleCreateNew = () => {
        setEditingUserId(null); 
        let defaultHospId = '';
        let defaultDepts = [];
        
        // --- ИСПРАВЛЕНИЕ: Ищем больницу по chief_doctor_id ---
        if (user?.role === 'chief_doctor') {
            const myHosp = hospitals.find(h => h.chief_doctor_id === user.id);
            if (myHosp) {
                defaultHospId = myHosp.id;
                defaultDepts = myHosp.departments || [];
            }
        }
        
        setFormData({ ...initialFormState, hospital_id: defaultHospId }); 
        setHospitalDepartments(defaultDepts);
        setView('form');
    };

    const handleEdit = (userObj) => {
        setEditingUserId(userObj.id);
        
        if (userObj.hospital_id) {
            const selectedHospital = hospitals.find(h => h.id === parseInt(userObj.hospital_id));
            setHospitalDepartments(selectedHospital?.departments || []);
        } else {
            setHospitalDepartments([]);
        }
        
        let region = ''; let city = userObj.city || ''; let street = userObj.address || '';
        if (userObj.address && userObj.address.includes('|||')) {
            const parts = userObj.address.split('|||');
            region = parts[0] || ''; city = parts[1] || ''; street = parts[2] || '';
        }

        setFormData({
            full_name: userObj.full_name || '', email: userObj.email || '', password: '',
            phone: userObj.phone || '+992 ', position: userObj.position || '',
            hospital_id: userObj.hospital_id || '', department: userObj.department || '',
            specialization: userObj.specialization || '', cabinet: userObj.cabinet || '',
            license_number: userObj.license_number || '', organization_name: userObj.organization_name || '',
            region: region, city: city, street: street, lab_type: userObj.lab_type || 'Клиническая'
        });
        setView('form');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);
        
        const payload = { ...formData, role: selectedRole };
        
        if (selectedRole === 'pharmacy' || selectedRole === 'lab') {
            payload.address = `${formData.region}|||${formData.city}|||${formData.street}`;
        }
        
        payload.hospital_id = payload.hospital_id ? parseInt(payload.hospital_id) : null;
        if (editingUserId && !payload.password) delete payload.password;

        delete payload.region;
        delete payload.street;

        try {
            if (editingUserId) await api.put(`/users/${editingUserId}`, payload);
            else await api.post('/users', payload);
            
            alert("Успешно сохранено!");
            setFormData(initialFormState);
            setHospitalDepartments([]);
            setView('list');
            fetchUsers(selectedRole); 
        } catch (error) {
            alert(error.response?.data?.error || "Ошибка сохранения");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if(!window.confirm("Вы уверены, что хотите удалить этого пользователя?")) return;
        try {
            await api.delete(`/users/${id}`);
            setUsersList(usersList.filter(u => u.id !== id));
        } catch (e) { 
            const errorMsg = e.response?.data?.error || "Неизвестная ошибка на сервере";
            alert(`Ошибка удаления!\nПричина: ${errorMsg}`); 
        }
    };

    const handleToggleStatus = async (userObj) => {
        const newStatus = !userObj.is_active;
        const action = newStatus ? "активировать" : "заблокировать";
        if(!window.confirm(`Вы хотите ${action} пользователя ${userObj.full_name}?`)) return;

        try {
            await api.put(`/users/${userObj.id}`, { is_active: newStatus });
            setUsersList(usersList.map(u => u.id === userObj.id ? { ...u, is_active: newStatus } : u));
        } catch (e) { alert("Ошибка изменения статуса"); }
    };

    const renderSpecificFields = () => {
        switch (selectedRole) {
            case 'admin': case 'moderator': case 'ministry': case 'chief_doctor':
                return (
                    <div style={{marginBottom: '24px'}}>
                        <label style={labelStyle}>Должность *</label>
                        <input name="position" style={inputStyle} value={formData.position} onChange={e => setFormData({...formData, position: e.target.value})} placeholder="Например: Старший специалист" required />
                    </div>
                );
            case 'doctor':
                return (
                    <>
                        <div style={gridStyle}>
                            <div>
                                <label style={labelStyle}>Выберите больницу *</label>
                                <select 
                                    name="hospital_id" 
                                    style={inputStyle} 
                                    value={formData.hospital_id} 
                                    onChange={e => {
                                        const hospId = e.target.value;
                                        setFormData({...formData, hospital_id: hospId, department: ''});
                                        const selectedHospital = hospitals.find(h => h.id === parseInt(hospId));
                                        setHospitalDepartments(selectedHospital?.departments || []);
                                    }} 
                                    required
                                    disabled={user?.role === 'chief_doctor'}
                                >
                                    <option value="">-- Выберите из списка --</option>
                                    {/* --- ИСПРАВЛЕНИЕ: Фильтруем список больниц для Главврача --- */}
                                    {hospitals
                                        .filter(h => user?.role === 'chief_doctor' ? h.chief_doctor_id === user.id : true)
                                        .map(h => <option key={h.id} value={h.id}>{h.name}</option>)
                                    }
                                </select>
                            </div>
                            <div>
                                <label style={labelStyle}>Отделение *</label>
                                <select 
                                    name="department" 
                                    style={inputStyle} 
                                    value={formData.department} 
                                    onChange={e => setFormData({...formData, department: e.target.value})} 
                                    required
                                    disabled={!formData.hospital_id}
                                >
                                    <option value="">-- Выберите отделение --</option>
                                    {hospitalDepartments.length > 0 ? (
                                        hospitalDepartments.map((dept, index) => (
                                            <option key={dept.id || index} value={dept.name}>{dept.name}</option>
                                        ))
                                    ) : (
                                        <option value="" disabled>Нет отделений</option>
                                    )}
                                </select>
                            </div>
                        </div>
                        <div style={gridStyle}>
                            <div>
                                <label style={labelStyle}>Специализация *</label>
                                <input name="specialization" style={inputStyle} value={formData.specialization} onChange={e => setFormData({...formData, specialization: e.target.value})} placeholder="Кардиолог" required />
                            </div>
                            <div>
                                <label style={labelStyle}>Кабинет *</label>
                                <input name="cabinet" style={inputStyle} value={formData.cabinet} onChange={e => setFormData({...formData, cabinet: e.target.value})} placeholder="№ 305" required />
                            </div>
                        </div>
                        <div style={{marginBottom: '24px'}}>
                             <label style={labelStyle}>Номер лицензии</label>
                             <input name="license_number" style={inputStyle} value={formData.license_number} onChange={e => setFormData({...formData, license_number: e.target.value})} placeholder="Лицензия №..." />
                        </div>
                    </>
                );
            case 'pharmacy':
                return (
                    <>
                        <div style={{marginBottom: '24px'}}>
                            <label style={labelStyle}>Название аптеки *</label>
                            <input name="organization_name" style={inputStyle} value={formData.organization_name} onChange={e => setFormData({...formData, organization_name: e.target.value})} placeholder='ООО "Здоровье"' required />
                        </div>
                        <div style={{marginBottom:'16px'}}>
                            <label style={labelStyle}>Область / Регион *</label>
                            <select name="region" style={inputStyle} value={formData.region} onChange={e=>setFormData({...formData, region: e.target.value, city: '', street: ''})} required>
                                <option value="">-- Выберите регион --</option>
                                {Object.keys(REGIONS_DATA).map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </div>
                        <div style={gridStyle}>
                            <div>
                                <label style={labelStyle}>Город / район *</label>
                                <select name="city" style={inputStyle} value={formData.city} onChange={e=>setFormData({...formData, city: e.target.value, street: ''})} disabled={!formData.region} required>
                                    <option value="">-- Выберите город --</option>
                                    {formData.region && REGIONS_DATA[formData.region].map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={labelStyle}>Улица, дом *</label>
                                <input name="street" style={inputStyle} value={formData.street} onChange={e => setFormData({...formData, street: e.target.value})} disabled={!formData.city} placeholder="ул. Рудаки 55" required />
                            </div>
                        </div>
                        <div style={{marginBottom: '24px'}}>
                            <label style={labelStyle}>Номер лицензии *</label>
                            <input name="license_number" style={inputStyle} value={formData.license_number} onChange={e => setFormData({...formData, license_number: e.target.value})} placeholder="№ Лицензии" required />
                        </div>
                    </>
                );
            case 'lab':
                return (
                    <>
                        <div style={gridStyle}>
                            <div>
                                <label style={labelStyle}>Название лаборатории *</label>
                                <input name="organization_name" style={inputStyle} value={formData.organization_name} onChange={e => setFormData({...formData, organization_name: e.target.value})} placeholder="Gemotest" required />
                            </div>
                            <div>
                                <label style={labelStyle}>Тип лаборатории *</label>
                                <select name="lab_type" style={inputStyle} value={formData.lab_type} onChange={e => setFormData({...formData, lab_type: e.target.value})}>
                                    <option>Клиническая</option><option>Диагностическая</option><option>Специализированная</option>
                                </select>
                            </div>
                        </div>
                        <div style={{marginBottom: '24px'}}>
                            <label style={labelStyle}>Выберите больницу (если прикреплена)</label>
                            <select name="hospital_id" style={inputStyle} value={formData.hospital_id} onChange={e => setFormData({...formData, hospital_id: e.target.value})}>
                                <option value="">-- Нет / Отдельная --</option>
                                {hospitals.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                            </select>
                        </div>
                        <div style={{marginBottom:'16px'}}>
                            <label style={labelStyle}>Область / Регион *</label>
                            <select name="region" style={inputStyle} value={formData.region} onChange={e=>setFormData({...formData, region: e.target.value, city: '', street: ''})} required>
                                <option value="">-- Выберите регион --</option>
                                {Object.keys(REGIONS_DATA).map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </div>
                        <div style={gridStyle}>
                            <div>
                                <label style={labelStyle}>Город / район *</label>
                                <select name="city" style={inputStyle} value={formData.city} onChange={e=>setFormData({...formData, city: e.target.value, street: ''})} disabled={!formData.region} required>
                                    <option value="">-- Выберите город --</option>
                                    {formData.region && REGIONS_DATA[formData.region].map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={labelStyle}>Улица, дом *</label>
                                <input name="street" style={inputStyle} value={formData.street} onChange={e => setFormData({...formData, street: e.target.value})} disabled={!formData.city} placeholder="ул. Ленина 12" required />
                            </div>
                        </div>
                        <div style={{marginBottom: '24px'}}>
                            <label style={labelStyle}>Номер лицензии *</label>
                            <input name="license_number" style={inputStyle} value={formData.license_number} onChange={e => setFormData({...formData, license_number: e.target.value})} placeholder="№ Лицензии" required />
                        </div>
                    </>
                );
            default: return null;
        }
    };

    if (view === 'cards') return (
        <div>
            <div className="page-header">
                <h2 className="page-title">Регистрация новых пользователей</h2>
                <p className="page-subtitle">Выберите тип пользователя для создания учетной записи</p>
            </div>
            <div className="registration-grid">
                {Object.keys(roleConfig)
                    .filter(key => canRegister(key))
                    .map((key) => (
                    <div key={key} className="reg-card" onClick={() => handleCardClick(key)} style={{ cursor: 'pointer' }}>
                        <div className="reg-card-header">
                            <div className="reg-icon-wrapper">{roleConfig[key].icon}</div>
                            <h4 className="reg-card-title">{roleConfig[key].title}</h4>
                        </div>
                        <p className="reg-card-desc">{roleConfig[key].desc}</p>
                    </div>
                ))}
            </div>
        </div>
    );

    if (view === 'list') {
        const filteredUsers = usersList.filter(u => {
            const s = searchQuery.toLowerCase();
            const matchSearch = (u.full_name || '').toLowerCase().includes(s) || 
                                (u.email || '').toLowerCase().includes(s) || 
                                (u.phone || '').toLowerCase().includes(s) || 
                                (u.organization_name || '').toLowerCase().includes(s);
            const matchStatus = statusFilter === 'all' || (statusFilter === 'active' ? u.is_active : !u.is_active);
            return matchSearch && matchStatus;
        });

        const visibleUsers = filteredUsers.slice(0, visibleCount);

        return (
            <div style={{maxWidth: '1200px'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem'}}>
                    <div style={{display: 'flex', gap: '1rem', alignItems:'center'}}>
                        <button onClick={() => setView('cards')} className="btn-secondary" style={{background:'white', border:'1px solid #ddd', width:'40px', height:'40px', borderRadius:'8px', cursor:'pointer'}}><ArrowLeft size={20}/></button>
                        <div>
                            <h2 style={{fontSize: '1.5rem', fontWeight: '600', margin: '0'}}>{roleConfig[selectedRole].title}</h2>
                            <p style={{margin: '0', color: '#6b7280', fontSize: '0.85rem'}}>Найдено: {filteredUsers.length} из {usersList.length}</p>
                        </div>
                    </div>
                    
                    <div style={{display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap'}}>
                        <div style={{position: 'relative'}}>
                            <Search size={16} style={{position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af'}} />
                            <input type="text" placeholder="Поиск..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={{...inputStyle, padding: '8px 12px 8px 32px', width: '220px', marginBottom: 0}} />
                        </div>
                        <select style={{...inputStyle, padding: '8px 12px', width: '150px', marginBottom: 0}} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                            <option value="all">Все статусы</option>
                            <option value="active">Активные</option>
                            <option value="blocked">Заблокированные</option>
                        </select>
                        <button className="btn-primary-action" onClick={handleCreateNew} style={{padding: '8px 16px', borderRadius: '8px', background: '#2563eb', color: 'white', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center'}}>
                            <Plus size={18} style={{marginRight: '8px'}}/> Добавить
                        </button>
                    </div>
                </div>

                <div className="card" style={{overflow: 'hidden', borderRadius: '12px', border: '1px solid #e5e7eb'}}>
                    <div className="card-body" style={{padding: 0}}>
                        {visibleUsers.length === 0 ? (
                            <div style={{textAlign:'center', padding:'40px', color:'#9ca3af'}}>
                                <User size={48} style={{margin:'0 auto 10px', opacity: 0.5}}/>
                                <p>Список пуст или ничего не найдено по фильтрам.</p>
                            </div>
                        ) : (
                            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem'}}>
                                <thead style={{backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb'}}>
                                    <tr>
                                        <th style={thStyle}>Пользователь</th>
                                        <th style={thStyle}>Контакты</th>
                                        <th style={thStyle}>Детали</th>
                                        <th style={thStyle}>Статус</th>
                                        <th style={thStyle}>Действия</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleUsers.map((u) => (
                                        <tr key={u.id} style={{borderBottom: '1px solid #f3f4f6', transition: 'background 0.2s'}} className="hover-row">
                                            <td style={tdStyle}>
                                                <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                                                    <div style={{width: '36px', height: '36px', borderRadius: '50%', background: '#eff6ff', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold'}}>
                                                        {u.full_name ? u.full_name.charAt(0) : '?'}
                                                    </div>
                                                    <div>
                                                        <div style={{fontWeight: '600', color: '#111827'}}>{u.full_name}</div>
                                                        <div style={{fontSize: '0.8rem', color: '#9ca3af'}}>ID: {u.id}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={tdStyle}>
                                                <div style={{color: '#374151'}}>{u.email}</div>
                                                <div style={{color: '#6b7280', fontSize: '0.85rem'}}>{u.phone}</div>
                                            </td>
                                            <td style={tdStyle}>
                                                <div style={{color: '#374151'}}>{u.organization_name || u.position || roleConfig[selectedRole].title}</div>
                                                <div style={{color: '#9ca3af', fontSize: '0.85rem'}}>{formatAddress(u.address) || u.city}</div>
                                            </td>
                                            <td style={tdStyle}>
                                                {u.is_active ? (
                                                    <span style={{background: '#dcfce7', color: '#166534', padding: '4px 10px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '500', display: 'inline-flex', alignItems: 'center', gap: '4px'}}>
                                                        <CheckCircle size={12}/> Активен
                                                    </span>
                                                ) : (
                                                    <span style={{background: '#fee2e2', color: '#991b1b', padding: '4px 10px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '500', display: 'inline-flex', alignItems: 'center', gap: '4px'}}>
                                                        <Lock size={12}/> Заблокирован
                                                    </span>
                                                )}
                                            </td>
                                            <td style={tdStyle}>
                                                <div style={{display: 'flex', gap: '8px'}}>
                                                    <button onClick={() => handleEdit(u)} title="Редактировать" style={actionBtnStyle('#3b82f6', '#eff6ff')}>
                                                        <Edit2 size={16}/>
                                                    </button>
                                                    <button onClick={() => handleToggleStatus(u)} title={u.is_active ? "Заблокировать" : "Разблокировать"} style={actionBtnStyle(u.is_active ? '#f59e0b' : '#10b981', u.is_active ? '#fffbeb' : '#ecfdf5')}>
                                                        {u.is_active ? <Lock size={16}/> : <Unlock size={16}/>}
                                                    </button>
                                                    <button onClick={() => handleDelete(u.id)} title="Удалить" style={actionBtnStyle('#ef4444', '#fef2f2')}>
                                                        <Trash2 size={16}/>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
                
                <style>{`
                    .hover-row:hover { background-color: #f9fafb; }
                `}</style>
            </div>
        );
    }

    return (
        <div style={{maxWidth: '100%'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem'}}>
                <button onClick={() => setView('list')} className="btn-secondary" style={{width:'40px', height:'40px', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', background: 'white'}}><ArrowLeft size={20}/></button>
            </div>

            <div className="card" style={{ padding: '40px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                <div style={{ borderBottom: '1px solid #eee', paddingBottom: '20px' }}>
                    <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginTop: '0', marginBottom: '5px' }}>
                        {editingUserId ? 'Редактирование пользователя' : `Новый ${roleConfig[selectedRole].title}`}
                    </h2>
                    <p style={{ color: '#6b7280' }}>
                        {editingUserId ? 'Измените данные и сохраните' : 'Заполните данные для создания учетной записи'}
                    </p>
                </div>

                <form onSubmit={handleSubmit}>
                    <div style={gridStyle}>
                        <div>
                            <label style={labelStyle}>ФИО *</label>
                            <input name="full_name" style={inputStyle} value={formData.full_name} onChange={e => setFormData({...formData, full_name: e.target.value})} placeholder="Иванов Иван" required />
                        </div>
                        <div>
                            <label style={labelStyle}>Email *</label>
                            <input name="email" type="email" style={inputStyle} value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="email@med.tj" required />
                        </div>
                    </div>
                    <div style={gridStyle}>
                        <div>
                            <label style={labelStyle}>{editingUserId ? "Новый пароль (необязательно)" : "Пароль *"}</label>
                            <input name="password" type="password" style={inputStyle} value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} placeholder={editingUserId ? "Оставьте пустым, чтобы не менять" : "******"} required={!editingUserId} />
                        </div>
                        <div>
                            <label style={labelStyle}>Телефон *</label>
                            <input name="phone" style={inputStyle} value={formData.phone} onChange={e => setFormData({...formData, phone: formatPhone(e.target.value)})} placeholder="+992 XXX-XX-XX-XX" required />
                        </div>
                    </div>

                    <hr style={{margin: '20px 0', border: 'none', borderTop: '1px solid #f3f4f6'}} />

                    {renderSpecificFields()}

                    <div style={{ display: 'flex', gap: '15px', marginTop: '30px' }}>
                        <button type="submit" disabled={isSubmitting} style={{ flex: 1, backgroundColor: isSubmitting ? '#374151' : '#05050f', color: 'white', padding: '14px', borderRadius: '8px', fontSize: '1rem', fontWeight: '500', border: 'none', cursor: isSubmitting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                            {isSubmitting ? <><Loader className="animate-spin" size={20} /> Сохранение...</> : (editingUserId ? <><CheckCircle size={20}/> Сохранить изменения</> : <><UserPlus size={20} /> Зарегистрировать</>)}
                        </button>
                        <button type="button" onClick={() => setView('list')} style={{ backgroundColor: 'white', color: '#111827', padding: '14px 30px', borderRadius: '8px', fontSize: '1rem', fontWeight: '500', border: '1px solid #d1d5db', cursor: 'pointer' }}>Отмена</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// СТИЛИ
const labelStyle = { display: 'block', fontSize: '0.9rem', fontWeight: '600', color: '#111827', marginBottom: '8px' };
const inputStyle = { width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#f9fafb', fontSize: '0.95rem', color: '#111827', outline: 'none' };
const gridStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' };
const thStyle = { textAlign: 'left', padding: '16px', fontSize: '0.85rem', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' };
const tdStyle = { padding: '16px', verticalAlign: 'middle' };
const actionBtnStyle = (color, bg) => ({
    border: 'none', background: bg, color: color, width: '32px', height: '32px', borderRadius: '8px', 
    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s'
});

export default Registration;